import React, { memo, useRef, useCallback, useEffect } from 'react';
import { bool } from 'prop-types';
import { useWtrDispatch } from 'redux/hooks';
import classNames from 'classnames';

import { KEY_ENTER, KEY_ESCAPE } from 'constants/keys';
import { useOnClickOutside } from 'hooks';
import { setBodyNoScroll } from 'utils/scroll';

import { dataLayer } from 'analytics/data-layer';
import { ChevronDown } from '@johnlewispartnership/wtr-ingredients/foundations/icons';
import MegaMenu from 'components/MegaMenu/Menus';

import { setMegaMenuActiveLevel } from 'redux/modules/page/actions/set-mega-menu-active-level';
import { setMegaMenuStart } from 'redux/modules/page/actions/set-mega-menu-start';
import useMenus from 'hooks/use-menus';
import { useMinSpend } from 'hooks/use-min-spend';
import SiteHeaderOverlay from '../../SiteHeaderOverlay/SiteHeaderOverlay';
import styles from './DropDownNav.scss';

const DropDownNav = memo(({ scrolled = false }) => {
  const menuButtonRef = useRef();
  const navigationRef = useRef();
  const menuRef = useRef();
  const { isMegaMenuOpen, toggleMegaMenu } = useMenus();
  const { showMinSpend } = useMinSpend();

  const dispatch = useWtrDispatch();

  const onOpen = menuRoot => {
    dispatch(setMegaMenuStart(menuRoot));
    toggleMegaMenu();
  };

  const onClose = useCallback(() => {
    toggleMegaMenu();
    dispatch(setMegaMenuActiveLevel(-1));
  }, [dispatch, toggleMegaMenu]);

  useEffect(() => {
    setBodyNoScroll(isMegaMenuOpen);
  }, [isMegaMenuOpen]);

  const handleClickOpener = (event, taxonomyRoot) => {
    event.preventDefault();

    if (isMegaMenuOpen) {
      onClose();
    } else {
      onOpen(taxonomyRoot);
      dataLayer.push({
        event: 'click_megamenu',
        mega_menu: {
          button_clicked: 'Groceries',
        },
      });
    }
  };

  const handleKeyDownOpener = event => {
    if (event.keyCode === KEY_ENTER) {
      setTimeout(() => {
        if (navigationRef.current) navigationRef.current.focusFirst();
      }, 200);
    }
  };

  const handleClickToClose = useCallback(() => {
    if (isMegaMenuOpen) {
      onClose();

      if (menuButtonRef?.current) {
        menuButtonRef.current.focus();
      }
    }
  }, [isMegaMenuOpen, onClose]);

  const handleClickOutside = useCallback(() => {
    if (isMegaMenuOpen) {
      onClose();
    }
  }, [isMegaMenuOpen, onClose]);

  useOnClickOutside(menuRef, handleClickOutside, menuButtonRef);

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      data-testid="DropDownNav"
      onKeyDown={event => {
        if (event.keyCode === KEY_ESCAPE) {
          handleClickToClose(event);
        }
      }}
    >
      <button
        aria-controls="megamenu"
        aria-expanded={isMegaMenuOpen}
        ref={menuButtonRef}
        aria-label="Toggle groceries menu"
        className={classNames(styles.btnMenu, {
          [styles.menuActive]: isMegaMenuOpen,
          [styles.scrolled]: scrolled,
          [styles.hasMinSpend]: showMinSpend,
        })}
        data-testid="site-header-browse-groceries"
        id="drop-down-nav"
        onClick={event => handleClickOpener(event, 'Groceries')}
        onKeyDown={event => handleKeyDownOpener(event)}
        type="button"
      >
        Groceries
        <ChevronDown className="icon" size="xsmall" />
      </button>
      <div ref={menuRef}>
        {isMegaMenuOpen && (
          <MegaMenu
            isOpen={isMegaMenuOpen}
            navigationRef={navigationRef}
            scrolled={scrolled}
            onToggle={toggleMegaMenu}
            handleClickToClose={handleClickToClose}
            showMinSpend={showMinSpend}
          />
        )}
      </div>
      <SiteHeaderOverlay isActive={isMegaMenuOpen} />
    </div>
  );
});

DropDownNav.displayName = 'DropDownNav';

DropDownNav.propTypes = {
  scrolled: bool,
};

export default DropDownNav;
